import { Director, View } from '@millicast/sdk';
import { memo, useEffect, useMemo, useRef } from 'react';

import { Card } from '../ui/card';
import { cn } from '../ui/utils';

export interface DolbyMillicastPlayerProps {
  className?: string;
  streamAccountId: string;
  streamName: string;
  subscriberToken?: string;
}

export const DolbyMillicastPlayer = memo(
  ({ className, streamAccountId, streamName, subscriberToken }: DolbyMillicastPlayerProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    const millicastView = useMemo(
      () =>
        new View('', () =>
          Director.getSubscriber({
            streamAccountId,
            streamName,
            subscriberToken,
          }),
        ),
      [streamAccountId, streamName, subscriberToken],
    );

    useEffect(() => {
      const video = videoRef.current;
      const onTrack = (event: RTCTrackEvent) => {
        if (video && event.streams[0]) {
          video.srcObject = event.streams[0];
        }
      };

      millicastView.on('track', onTrack);

      void (async () => {
        try {
          await millicastView.connect();
          console.log('Connection successful');
        } catch (e) {
          console.log('Connection failed, handle error', e);
        }
      })();

      return () => {
        millicastView.removeListener('track', onTrack);
        millicastView.stop();
      };
    }, [millicastView]);

    return (
      <Card className={cn('relative aspect-video overflow-hidden', className)}>
        <video
          ref={videoRef}
          autoPlay
          className="absolute top-0 left-0 h-full w-full object-cover"
          controls
          data-testid="dolby-io-player"
          muted
        />
      </Card>
    );
  },
);

DolbyMillicastPlayer.displayName = 'DolbyIoPlayer';
